/**
 * Kalyanawaran — Subscription Plan Configuration
 *
 * This is the single source of truth for all plan definitions.
 * To add a new feature: add an entry to PLAN_FEATURES.
 * To change a limit: edit the value inside PLANS[planKey].features[featureKey].
 *
 * Feature value conventions:
 *   true        → fully included (✅)
 *   false       → not included (❌)
 *   number      → numeric limit (e.g. 5 searches/day)
 *   "unlimited" → no limit
 *   string      → descriptive label (e.g. "Email only")
 */

// ─── Feature Metadata ────────────────────────────────────────────────────────
// Defines display label, category, and how the value should be rendered in UI.

export const PLAN_FEATURES = {
  profileCreation: {
    label: "Profile Creation",
    category: "Basic Features",
    description: "Create and manage your matrimony profile",
    renderType: "boolean",
  },
  searchProfiles: {
    label: "Profile Views",
    category: "Matching & Views",
    description: "Number of profile views allowed per day",
    renderType: "limit",
    unit: " profile view / day",
  },
  featuredProfile: {
    label: "Featured Profile",
    category: "Visibility",
    description: "Get highlighted on the search results and home showcase",
    renderType: "boolean",
  },
  dailyCost: {
    label: "Daily Cost",
    category: "Pricing Detail",
    description: "Breakdown of daily cost",
    renderType: "text",
  },
  newFeatureAccess: {
    label: "New Feature Access",
    category: "Perks",
    description: "Access to newly released platform features",
    renderType: "text",
  },
  customerSupport: {
    label: "Customer Support",
    category: "Support",
    description: "Support priority and channels available",
    renderType: "text",
  },
};

// ─── Plan Definitions ─────────────────────────────────────────────────────────

export const PLANS = {
  free: {
    key: "free",
    name: "Free Plan",
    tagline: "Get started on Kalyanawaran for free",
    price: 0,
    currency: "INR",
    billingCycle: null,
    badge: "Free Tier",
    isLive: true,
    isComingSoon: false,
    highlightColor: "#6B7280", // gray
    features: {
      profileCreation: true,
      searchProfiles: 1,
      featuredProfile: false,
      dailyCost: "Free",
      newFeatureAccess: "Standard Access",
      customerSupport: "Standard Support",
    },
  },

  silver: {
    key: "silver",
    title: "✨ SHUBH ARAMBH (SILVER PLAN) ✨",
    name: "Shubh Arambh (Silver Plan)",
    tagline: "Celebrate a new beginning at just ₹1/Day!",
    originalPrice: 1000,
    price: 365,
    savingsPercentage: "63.5%",
    currency: "INR",
    billingCycle: "Year",
    badge: "Special Launch Offer",
    isLive: true,
    isComingSoon: false,
    highlightColor: "#0EA5E9", // sky blue / silver accent
    features: {
      profileCreation: true,
      searchProfiles: 5,
      featuredProfile: true,
      dailyCost: "₹1 (Paid Annually)",
      newFeatureAccess: "Early / Beta Access",
      customerSupport: "Priority (Call / WhatsApp / Email)",
    },
  },

  gold: {
    key: "gold",
    name: "Gold Plan",
    tagline: "Advanced matching features and top priority listing",
    price: 1999,
    currency: "INR",
    billingCycle: "Year",
    badge: "Coming Soon",
    isLive: false,
    isComingSoon: true,
    highlightColor: "#C9A84C", // gold
    features: {
      profileCreation: true,
      searchProfiles: 15,
      featuredProfile: true,
      dailyCost: "Coming Soon",
      newFeatureAccess: "Full Access",
      customerSupport: "VIP Support",
    },
  },

  platinum: {
    key: "platinum",
    name: "Platinum Plan",
    tagline: "Ultimate personalized matrimony assistance & unlimited reach",
    price: 3499,
    currency: "INR",
    billingCycle: "Year",
    badge: "Coming Soon",
    isLive: false,
    isComingSoon: true,
    highlightColor: "#8B5CF6", // purple / platinum
    features: {
      profileCreation: true,
      searchProfiles: 50,
      featuredProfile: true,
      dailyCost: "Coming Soon",
      newFeatureAccess: "VIP Access",
      customerSupport: "Dedicated Relationship Manager",
    },
  },
};

// Ordered list of plan keys for consistent rendering
export const PLAN_ORDER = ["free", "silver", "gold", "platinum"];

// ─── Helper Utilities ─────────────────────────────────────────────────────────

/**
 * Get the display string for a feature value in a given plan.
 * e.g. getFeatureDisplay("searchProfiles", "gold") → "5/day"
 */
export const getFeatureDisplay = (featureKey, planKey) => {
  const feature = PLAN_FEATURES[featureKey];
  const plan = PLANS[planKey];
  if (!feature || !plan) return "—";

  const value = plan.features[featureKey];

  if (feature.renderType === "boolean") {
    return value === true ? "included" : "not-included";
  }
  if (feature.renderType === "limit") {
    if (value === "unlimited") return "unlimited";
    if (typeof value === "number") return `${value}${feature.unit || ""}`;
    return "—";
  }
  if (feature.renderType === "text") {
    return value || "—";
  }
  return String(value);
};

/**
 * Check if a user with the given plan is allowed to use a feature.
 * Returns { allowed: boolean, limit: number|"unlimited"|null }
 */
export const checkFeatureAccess = (featureKey, planKey) => {
  const plan = PLANS[planKey];
  if (!plan) return { allowed: false, limit: null };

  const value = plan.features[featureKey];
  if (value === false || value === undefined)
    return { allowed: false, limit: null };
  if (value === true) return { allowed: true, limit: null };
  if (value === "unlimited") return { allowed: true, limit: "unlimited" };
  if (typeof value === "number") return { allowed: value > 0, limit: value };
  return { allowed: Boolean(value), limit: null };
};

/**
 * Format price for display
 * e.g. formatPrice(999, "INR") → "₹999"
 */
export const formatPrice = (price, currency = "INR") => {
  if (price === 0) return "Free";
  const symbols = { INR: "₹", USD: "$" };
  return `${symbols[currency] || currency} ${price.toLocaleString("en-IN")}`;
};
